import type { PresentationPreview } from 'paintscout'

import { PRESENTATION_CANVAS_ID } from './presentationEditorDom'

/**
 * Marks a rendered block wrapper in `Presentation.tsx` with its own `key`.
 *
 * The wrapper already carries `id={block.key}`, which is what `scrollToPresentationKey` looks
 * up, but an `id` cannot be queried as a *set* — there is no way to ask "every block on the
 * page currently rendered" without knowing the keys first. This attribute gives one
 * `querySelectorAll`, so the anchor picker below needs no presentation tree at all.
 */
export const BLOCK_KEY_ATTRIBUTE = 'data-block-key'

/**
 * How far below the viewport top a block has to reach before it counts as "in view".
 *
 * Clears the fixed 60px `PresentationEditTopBar`, roughly — the same allowance
 * `presentationCanvasScroll.ts`'s `SCROLL_TOP_INSET` uses for the reverse operation.
 */
export const VIEWPORT_ANCHOR_INSET = 48

/** Sub-pixel layout rounding must not disqualify a block that is visibly flush with the edge. */
const EDGE_TOLERANCE = 1

/** Sub-pixel scroll positions (zoomed or high-DPI screens) still count as fully scrolled up. */
const AT_TOP_TOLERANCE = 1

export interface BlockRect {
  key: string
  /** Viewport-relative top, i.e. `getBoundingClientRect().top`. */
  top: number
  /** Viewport-relative bottom, i.e. `getBoundingClientRect().bottom`. */
  bottom: number
}

/**
 * The block to line up with the top of the viewport when the user switches between the editor and
 * the preview (ENG-2004/#23984).
 *
 * - Exactly one block on screen — that block.
 * - Several — the highest one that is completely on screen. If none is completely on screen (every
 *   visible block is cut off at the top or bottom), the highest visible one.
 * - None on screen (scrolled past the last block) — the last block.
 *
 * "On screen" means below `inset` (the fixed chrome at the top) and above `viewportHeight`.
 *
 * Split out from the DOM read so the geometry is testable. Blocks are expected in document order,
 * which is what `querySelectorAll` returns.
 */
export function pickAnchorBlockKey(args: { blocks: BlockRect[]; viewportHeight: number; inset?: number }): string {
  const { blocks, viewportHeight, inset = VIEWPORT_ANCHOR_INSET } = args

  if (!blocks?.length) {
    return ''
  }

  const visible = blocks.filter((block) => block.bottom > inset && block.top < viewportHeight)

  if (!visible.length) {
    return blocks.at(-1).key
  }

  if (visible.length === 1) {
    return visible[0].key
  }

  const fullyVisible = visible.find(
    (block) => block.top >= inset - EDGE_TOLERANCE && block.bottom <= viewportHeight + EDGE_TOLERANCE
  )

  return (fullyVisible ?? visible[0]).key
}

/**
 * Whether a window is scrolled fully to the top. Switching between the editor and the preview
 * keeps this state instead of aligning a block: a block at the top of the screen is not always at
 * scroll position 0 (the nav header and the canvas chrome sit above it), so aligning it would scroll
 * a user who never moved.
 */
export function isScrolledToTop(win: Window = window): boolean {
  return win.scrollY <= AT_TOP_TOLERANCE
}

/**
 * Height of the fixed nav bar in a rendered presentation document, 0 when there is none. Content
 * scrolls under it in the preview iframe, so it is the iframe's counterpart of the editor canvas's
 * fixed `VIEWPORT_ANCHOR_INSET`.
 */
export function getHeaderHeight(doc: Document = document): number {
  return doc.getElementsByTagName('header')?.[0]?.offsetHeight ?? 0
}

/**
 * Reads the editor canvas (or a preview document) and returns the key of the block to anchor on.
 * See `pickAnchorBlockKey` for the rule.
 *
 * Only the active page's blocks are ever in the DOM (#21990), so the key this returns also
 * identifies the page — callers resolve it with `resolvePageForKey` rather than tracking a page
 * key separately.
 *
 * Must be called while the canvas is still mounted: preview mode replaces it with an iframe, so a
 * read taken after the swap finds nothing and returns `''`.
 */
export function getAnchorBlockKey(args?: { doc?: Document; inset?: number }): string {
  const { doc = document, inset } = args ?? {}
  const canvas = doc.getElementById(PRESENTATION_CANVAS_ID)

  if (!canvas) {
    return ''
  }

  const blocks: BlockRect[] = Array.from(canvas.querySelectorAll(`[${BLOCK_KEY_ATTRIBUTE}]`)).map((element) => {
    const { top, bottom } = element.getBoundingClientRect()

    return { key: element.getAttribute(BLOCK_KEY_ATTRIBUTE) ?? '', top, bottom }
  })

  return pickAnchorBlockKey({ blocks, viewportHeight: (doc.defaultView ?? window).innerHeight, inset })
}

/**
 * The anchor the presentation context should carry after a preview-mode change.
 *
 * Three cases:
 * - leaving preview — read the iframe (ENG-2004/#23984), the mirror of the edit → preview case
 *   below: it is still mounted at the moment this runs, since the swap back to the canvas happens
 *   on the render that follows;
 * - edit → preview — read the canvas, which is the only moment it is still mounted;
 * - preview → another device — read the iframe again, before it resizes, so the preview can
 *   re-align that block after the reflow.
 *
 * Both DOM reads are callbacks rather than values so they only ever run on their own capture
 * branch. Called unconditionally, a device switch inside preview would measure an unmounted canvas
 * and overwrite a good anchor with `''`; leaving preview without an editor mounted yet would do the
 * same in reverse.
 */
export function nextPreviewAnchorKey(args: {
  nextPreview: PresentationPreview
  currentPreview: PresentationPreview
  currentAnchorKey: string
  readVisibleBlockKey: () => string
  readVisiblePreviewBlockKey?: () => string
}): string {
  const { nextPreview, currentPreview, currentAnchorKey, readVisibleBlockKey, readVisiblePreviewBlockKey } = args

  if (!nextPreview || nextPreview === 'none') {
    return readVisiblePreviewBlockKey ? readVisiblePreviewBlockKey() : ''
  }

  const leavingEditor = !currentPreview || currentPreview === 'none'

  if (leavingEditor) {
    return readVisibleBlockKey()
  }

  // preview → another device: the frame is about to resize and reflow, so re-read the block in view
  // now, while it still has the old width. Falls back to the standing anchor when the frame has
  // nothing to read (not loaded yet), so a good anchor is never overwritten with `''`.
  return readVisiblePreviewBlockKey?.() || currentAnchorKey || ''
}
